/**
 * This function gets the wording to express minutes in the corresponding language,
 * depending on its number (a single minute or more than one).
 *
 * @param {Integer} minutes - the number of minutes.
 *
 * @return {String} the translation for the word minutes - or minute -.
 */

export const getMinutes = (minutes) => {
  switch (true) {
    case minutes === 1:
      return I18n.t("datetime.distance_in_words.x_minutes.one");
    case minutes > 1:
      return I18n.t("datetime.distance_in_words.x_minutes.other", {
        count: minutes,
      });
    default:
      return 0;
  }
};

/**
 * This function gets the wording to express seconds in the corresponding language,
 * depending on its number (a single second or more than one).
 *
 * @param {Integer} seconds - the number of seconds.
 *
 * @return {String} the translation for the word seconds - or second -.
 */

export const getSeconds = (seconds) => {
  switch (true) {
    case seconds === 1:
      return I18n.t("datetime.distance_in_words.x_seconds.one");
    case seconds > 1:
      return I18n.t("datetime.distance_in_words.x_seconds.other", {
        count: seconds,
      });
    default:
      return 0;
  }
};

/**
 * This function flattens an object recursively, to obtain an object of objects with only one
 * nesting level.
 *
 * @param {Object} nestedMessages - the Object with nested elements.
 * @param {String} prefix - a prefix to use for the new key, initialized to an empty char.
 *
 * @return {Object}
 */

export const flattenMessages = (nestedMessages, prefix = "") => {
  if (nestedMessages === null) {
    return {};
  }
  return Object.keys(nestedMessages).reduce((messages, key) => {
    const value = nestedMessages[key];
    const prefixedKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === "string") {
      Object.assign(messages, { [prefixedKey]: value });
    } else if (typeof value !== "undefined") {
      Object.assign(messages, flattenMessages(value, prefixedKey));
    }

    return messages;
  }, {});
};

/**
 * This function checks the data attribute called 'locale' in the body tag.
 *
 * @return {String} "es", "pt", "en", "de" or null otherwise
 */

export const getLang = document.querySelector("body")?.dataset?.locale;

/**
 * Creates an ID using random number between 0 and 1000.
 *
 * @param {String} prefix - prefix for the ID. Will use "id" as default.
 *
 * @returns {String} ID with structure <prefix><number>.
 */
export const createID = (prefix = "id") => {
  return `${prefix}${Math.floor(Math.random() * 1000 + 1)}`;
};

/**
 * This function checks if the current element or one of its children has
 * a certain CSS class.
 *
 * @param {Element} element - the DOM Element to check.
 * @param {String} classname - the classname to find.
 *
 * @return {Boolean|null}
 */

export const hasSomeChildWithClass = (element, classname) => {
  if (element.classList && element.classList.contains(classname)) {
    return true;
  }

  if (element.querySelector(`.${classname}`)) {
    return true;
  }

  return false;
};

/*
 * This function checks recursevely if the current element or one of its ancestors has
 * a certain CSS class.
 *
 * @param {Element} element - the DOM Element to check.
 * @param {String} classname - the classname to find.
 *
 * @return {Boolean|null}
 */

export const hasSomeParentWithClass = (element, classname) => {
  if (element.classList && element.classList.contains(classname)) {
    return true;
  }
  return element.parentNode && hasSomeParentWithClass(element.parentNode, classname);
};

/**
 * This function creates the configuration data to be sent in the POST requests
 * retrieving the partial that will be inject in the modal element
 *
 * @param {Object} dataset - an object with the params needede to build the URL to be requested.
 */

export const getRequestConfiguration = (baseURL, dataset) => {
  let data = dataset;

  if (!data || typeof data !== "object") {
    data = {};
  }

  let route = baseURL;
  let index = 0;

  for (const [key, value] of Object.entries(data)) {
    if (index === 0) {
      route += "?";
    }

    if (index !== 0) {
      route += "&";
    }

    route += `${key}=${value}`;
    index += 1;
  }

  const configuration = {
    headers: {
      Accept: "text/html, */*; q=0.01",
      "X-Requested-With": "XMLHttpRequest",
    },
    method: "GET",
    route,
  };

  return configuration;
};

/**
 * Gets next sibling of the element, matching a selector.
 *
 * @param {Element} element - the DOM element.
 * @param {string} selector
 *
 * @returns {Element}
 */

export const getNextSibling = (element, selector) => {
  // Get the next sibling element
  let sibling = element.nextElementSibling;

  // If there's no selector, return the first sibling
  if (!selector) return sibling;

  // If the sibling matches our selector, use it
  // If not, jump to the next sibling and continue the loop
  while (sibling) {
    if (sibling.matches(selector)) {
      return sibling;
    }
    sibling = sibling.nextElementSibling;
  }
};

/**
 * An approach to make async functions in an optimal way
 *
 * @returns {Promise}
 */
export const rafAsync = () => {
  return new Promise((resolve) => {
    requestAnimationFrame(resolve);
  });
};

/**
 * Inserts a script tag in the page's body returning a Promise.
 * @param {String} - url.
 * @returns {Promise}
 */
export const insertScriptAsync = ({ url, crossOrigin = null }) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.defer = true;

    if (crossOrigin) {
      script.crossOrigin = crossOrigin;
    }

    script.onload = () => resolve(script);
    script.onerror = (e) => reject(e.error);
    document.body.appendChild(script);
  });
};

/**
 * Removes a node from the page's body.
 * @param {Node} - node.
 */
export const removeNode = (node) => {
  if (document.body.contains(node)) {
    document.body.removeChild(node);
  }
};

/**
 * Awaits for ReCaptcha component to be available
 *
 * @returns {true}
 */
export const awaitForReCaptchaLoad = () => {
  if (window.grecaptcha === undefined) {
    return rafAsync().then(() => awaitForReCaptchaLoad());
  }
  return Promise.resolve(true);
};
